// Benefit writes echo the form's original opaque token. Workspace conflicts retain the
// draft for review; shared callers outside the workspace keep their existing reload policy.
import { toast } from "sonner"
import { captureStaffEpoch, StaffSessionExpiredError } from "@/services/staffTransportSession"
import { commandFailure, isStaffCommandManaged } from "@/services/staffCommands"
import { api, ApiError } from "@/lib/api"
import type { BenefitDetailDto } from "@/lib/benefit-types"
import { runBenefitSave, type BenefitSaveOutcome } from "@/lib/benefit-save-operation"
export type { BenefitSaveOutcome } from "@/lib/benefit-save-operation"

/** Direct financial/lifecycle callers use the same outcome policy without changing builders. */
export function handledWorkspaceFailure(benefitId: string, error: unknown): boolean {
  if (error instanceof StaffSessionExpiredError) return true
  if (!isStaffCommandManaged(`/api/benefitapplications/${benefitId}`)) return false
  toast.error(commandFailure(error).message)
  return true
}

export function saveBenefitChange(
  benefitId: string,
  onUpdated: (benefit: BenefitDetailDto) => void,
  write: () => Promise<BenefitDetailDto>,
  successMessage: string,
): Promise<BenefitSaveOutcome> {
  const epoch = captureStaffEpoch()
  const managed = isStaffCommandManaged(`/api/benefitapplications/${benefitId}`)
  return runBenefitSave({
    retainConflict: managed,
    isCurrent: () => captureStaffEpoch() === epoch,
    write,
    reload: () => api.get<BenefitDetailDto>(`/api/benefitapplications/${benefitId}`),
    onUpdated: managed ? () => {} : onUpdated,
    onSaved: (updated) => {
      if (updated.identitySave?.reviewRequired) {
        toast.warning(`Household member updated. Identity changes require review (request #${updated.identitySave.changeRequestId}).`)
      } else {
        toast.success(successMessage)
      }
    },
    onError: (message) => toast.error(message),
    classify: (err) => ({
      status: err instanceof ApiError ? err.status : undefined,
      message: managed ? commandFailure(err).message : err instanceof Error ? err.message : "The change could not be saved.",
      fieldErrors: err instanceof ApiError && err.status === 422 ? err.fieldErrors : {},
    }),
  })
}
